import { triniumApi } from '@api/Trinium/slice';
import { IsActiveParameter, StartsWithParameter } from '@api/Trinium/types';
import {
	ContainerProvider,
	ContainerSize,
	ContainerType,
	EdiRejectionReason,
	HazmatClass,
	HazmatUnNumber,
	NotificationType,
	Port
} from './types';
import { defaultSortComparator } from '@components/DataGrid/types';

export const lookupSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchContainerProviders: builder.query<ContainerProvider[], StartsWithParameter & IsActiveParameter>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: (queryParameters) => ({
					url: '/lookup/containerproviders',
					params:
						queryParameters.startsWith || queryParameters.isActive
							? {
									...queryParameters
								}
							: undefined
				}),
				transformResponse: (response: ContainerProvider[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.name, b.name);
					});
				}
			}),

			fetchContainerSizes: builder.query<ContainerSize[], undefined>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: () => ({
					url: '/lookup/containersizes'
				}),
				transformResponse: (response: ContainerSize[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.size, b.size);
					});
				}
			}),

			fetchContainerTypes: builder.query<ContainerType[], undefined>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: () => ({
					url: '/lookup/containertypes'
				}),
				transformResponse: (response: ContainerType[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.type, b.type);
					});
				}
			}),

			fetchHazmatClasses: builder.query<HazmatClass[], StartsWithParameter>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: (queryParameters) => ({
					url: '/lookup/hazmatclasses',
					params: queryParameters.startsWith
						? {
								...queryParameters
							}
						: undefined,
					tranformResponse: (response: HazmatClass[]) => {
						/** isActive has a default value of true and therefore is not returned in the API response  */
						return [
							...response.map(({ isActive, ...hCls }) => ({
								...hCls,
								isActive: isActive === false ? isActive : true
							}))
						];
					}
				})
			}),

			fetchHazmatUnNumbers: builder.query<HazmatUnNumber[], StartsWithParameter>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: (queryParameters) => ({
					url: '/lookup/hazmatunnos',
					params: queryParameters.startsWith
						? {
								...queryParameters
							}
						: undefined,
					tranformResponse: (response: HazmatUnNumber[]) => {
						/** isActive has a default value of true and therefore is not returned in the API response  */
						return [
							...response.map(({ isActive, ...hUn }) => ({
								...hUn,
								isActive: isActive === false ? isActive : true
							}))
						];
					}
				})
			}),

			fetchNotificationTypes: builder.query<NotificationType[], undefined>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: () => ({
					url: '/lookup/notificationTypes'
				}),
				transformResponse: (response: NotificationType[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.name, b.name);
					});
				}
			}),

			fetchPorts: builder.query<Port[], undefined>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: () => ({
					url: '/lookup/ports'
				}),
				transformResponse: (response: Port[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.name, b.name);
					});
				}
			}),

			fetchEdiRejectionReasons: builder.query<EdiRejectionReason[], undefined>({
				keepUnusedDataFor: Number.MAX_VALUE,
				query: () => ({
					url: '/lookup/edirejectionreasons'
				}),
				transformResponse: (response: EdiRejectionReason[]) => {
					return response.sort((a, b) => {
						return defaultSortComparator(a.description, b.description);
					});
				}
			})
		};
	}
});

export const {
	useFetchContainerProvidersQuery,
	useFetchContainerSizesQuery,
	useFetchContainerTypesQuery,
	useFetchHazmatClassesQuery,
	useFetchHazmatUnNumbersQuery,
	useFetchNotificationTypesQuery,
	useFetchPortsQuery,
	useFetchEdiRejectionReasonsQuery
} = lookupSlice;
